import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { RootState } from 'types';
import { updateFcmTokenUser } from 'store/auth/authSlice';
import { getUserFromLocalStorage } from 'helpers/localStorage';
import { generateToken } from '../../../services/firebase';
import { resolveUserEmail } from '../../../services/fcmToken';
import {
  detectBrowser,
  getPermissionState,
  requestPermissionSafely,
  subscribeToPermissionChange,
  TBrowserKey,
  TPermissionState,
} from '../../../services/notificationPermission';
import { EPath } from '../../routes/routesConfig';

/**
 * `prompt`     - asking the user to enable notifications
 * `requesting` - the browser prompt is open, waiting for an answer
 * `timeout`    - the browser prompt was left unanswered
 * `checking`   - re-reading the permission after a settings change
 */
export type TGatePhase = 'prompt' | 'requesting' | 'timeout' | 'checking';

const RECHECK_DELAY_MS = 600;

/**
 * Screens the gate must never cover.
 *
 * These are the employee web's first-login steps. A non-dismissible dialog
 * there would block onboarding outright, and on a browser that cannot do web
 * push at all (iOS Safari outside an installed PWA) the user could never get
 * past it. Everywhere else the gate is enforced, so a permission revoked
 * mid-session brings it straight back.
 */
const EXCLUDED_PATHS: string[] = [EPath.employeeIdentity, EPath.employeeInitialPass];

export const useNotificationGate = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { currentUser } = useSelector((state: RootState) => state.auth);

  const [permission, setPermission] = useState<TPermissionState>(() => getPermissionState());
  const [phase, setPhase] = useState<TGatePhase>('prompt');
  const [stillBlocked, setStillBlocked] = useState(false);
  const [browser] = useState<TBrowserKey>(() => detectBrowser());
  // Deliberately not persisted: a blocked user should be reminded again on
  // the next page load, so closing the dialog only clears it for now.
  //
  // Cleared only by a real Permissions API change event, never by a re-read:
  // resetting on any read that came back 'default' made the dialog reappear on
  // every navigation in Safari private browsing. Otherwise a reload is what
  // brings the dialog back.
  const [dismissed, setDismissed] = useState(false);

  const isMountedRef = useRef(true);
  const tokenSyncedRef = useRef(false);
  const previousPermissionRef = useRef<TPermissionState>(permission);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Redux state is the source of truth once loaded, but on a fresh reload the
  // gate can mount before `getCurrentUser` resolves, so fall back to storage.
  // Memoised because reading storage on every render would give `user` a new
  // identity each time and re-run the effects below.
  const user = useMemo(() => {
    if (currentUser && Object.keys(currentUser).length) return currentUser;
    return getUserFromLocalStorage();
  }, [currentUser]);
  const hasUser = !!(user && Object.keys(user).length);

  /**
   * Closable once the user has engaged with the dialog at least once and the
   * permission still is not granted:
   *
   * - 'unsupported': no PushManager at all, nothing to attempt.
   * - 'denied': a prompt can no longer be raised from here.
   * - after a timeout: the prompt was suppressed by the browser, left
   *   unanswered, or answered in a window that does not keep the answer.
   *   Firefox private browsing does not persist permissions, so a block there
   *   reads back as 'default' and would otherwise loop with no way out.
   *
   * Every state that shows the instructions is therefore closable; only the
   * untouched first prompt stays enforced, so the request is put to the user
   * once. Closing only clears it for the current page.
   */
  const canDismiss = permission === 'unsupported' || permission === 'denied' || phase === 'timeout';

  // A dismissal is honoured on its own, not re-checked against canDismiss:
  // that flag is rebuilt from scratch on every page load, so pairing the two
  // made a stored dismissal evaporate on reload and the dialog came back on
  // each one. onDismiss is the gate on who may dismiss in the first place.
  const visible =
    hasUser &&
    !EXCLUDED_PATHS.includes(location.pathname) &&
    permission !== 'granted' &&
    !dismissed;

  /**
   * Runs after permission is granted. Failures here are logged and swallowed:
   * the user is already inside the app and must never be blocked by the token
   * round trip.
   */
  const syncFcmToken = useCallback(async () => {
    if (tokenSyncedRef.current) return;
    tokenSyncedRef.current = true;
    try {
      const token = await generateToken();
      const email = resolveUserEmail(user);
      if (token && email) {
        dispatch(updateFcmTokenUser({ fcmToken: token, email }));
      }
    } catch (error) {
      console.error('FCM token sync after permission grant failed: ', error);
    }
  }, [dispatch, user]);

  useEffect(() => {
    const previous = previousPermissionRef.current;
    previousPermissionRef.current = permission;
    // Sync on the transition into 'granted', not on being granted: a permission
    // already granted at login is handled by the app bootstrap, while a
    // permission fixed later in browser settings reaches us only here. Keying
    // off "did the user press our button" missed that second case entirely,
    // leaving the user with notifications allowed but no token on the server.
    if (permission === 'granted' && previous !== 'granted' && hasUser) {
      syncFcmToken();
    }
  }, [permission, hasUser, syncFcmToken]);

  // Runs for the whole session, not just while the dialog is open: permission
  // granted earlier can be revoked from browser settings at any time, and that
  // has to bring the gate back rather than go unnoticed until a page reload.
  useEffect(() => {
    if (!hasUser) return undefined;
    return subscribeToPermissionChange((next, source) => {
      if (!isMountedRef.current) return;
      setPermission(next);
      // Only an actual Permissions API change event means the user altered the
      // setting, which makes an earlier dismissal stale. A plain re-read must
      // not clear it: that is what made the dialog reappear on every
      // navigation in Safari private browsing, where the read is unstable and
      // no change event is delivered at all.
      if (source === 'event') setDismissed(false);
    });
  }, [hasUser]);

  // Belt and braces for browsers that fire neither the Permissions API change
  // event nor a focus event after a setting is flipped: re-read the permission
  // on every navigation, so any later action re-checks it.
  useEffect(() => {
    if (!hasUser) return;
    setPermission(getPermissionState());
  }, [location.pathname, hasUser]);

  // A permission that is no longer granted starts the dialog over from the top,
  // and allows the token to be re-sent once it is granted again.
  useEffect(() => {
    if (permission === 'granted') return;
    setPhase('prompt');
    setStillBlocked(false);
    tokenSyncedRef.current = false;
  }, [permission]);

  const requestPermission = useCallback(async () => {
    setStillBlocked(false);
    setPhase('requesting');

    const result = await requestPermissionSafely();
    if (!isMountedRef.current) return;

    if (result === 'timeout' || result === 'default') {
      // Unanswered prompt, or an error the browser reported as no decision.
      setPhase('timeout');
      setPermission(getPermissionState());
      return;
    }

    setPhase('prompt');
    setPermission(result);
  }, []);

  const recheckPermission = useCallback(async () => {
    setStillBlocked(false);
    setPhase('checking');

    await new Promise((resolve) => setTimeout(resolve, RECHECK_DELAY_MS));
    if (!isMountedRef.current) return;

    const next = getPermissionState();
    setPermission(next);
    setPhase('prompt');
    setStillBlocked(next !== 'granted');
  }, []);

  // A denied or unsupported browser cannot show the prompt again, so the only
  // way forward is a settings change followed by a re-check.
  const needsSettingsChange = permission === 'denied' || permission === 'unsupported';

  const onDismiss = useCallback(() => {
    if (!canDismiss) return;
    setDismissed(true);
  }, [canDismiss]);

  const onPrimaryAction = useCallback(() => {
    if (phase === 'requesting' || phase === 'checking') return;
    if (needsSettingsChange) {
      recheckPermission();
      return;
    }
    requestPermission();
  }, [phase, needsSettingsChange, recheckPermission, requestPermission]);

  return {
    visible,
    permission,
    phase,
    browser,
    stillBlocked,
    needsSettingsChange,
    isBusy: phase === 'requesting' || phase === 'checking',
    onPrimaryAction,
    canDismiss,
    onDismiss,
  };
};
